import { useCallback, useEffect, useRef, useState } from "react";
import { loadAuthClient, type AuthClient } from "./authClientLoader";

export type AuthSession = Awaited<ReturnType<AuthClient["getSession"]>>["data"];

type SessionValue = AuthSession | null;

type AuthSessionOptions = {
  eager?: boolean;
  idleDelay?: number;
};

type AuthSessionState = {
  data: SessionValue;
  isPending: boolean;
};

let cachedSession: SessionValue | undefined;
let pendingRequest: Promise<SessionValue> | null = null;
let pendingToken: symbol | null = null;
const listeners = new Set<() => void>();

function notify() {
  for (const listener of listeners) listener();
}

export function readAuthSession() {
  return cachedSession;
}

export function subscribeAuthSession(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function invalidateAuthSession() {
  cachedSession = undefined;
  pendingRequest = null;
  pendingToken = null;
  notify();
}

export function loadAuthSession(): Promise<SessionValue> {
  if (cachedSession !== undefined) return Promise.resolve(cachedSession);
  if (pendingRequest) return pendingRequest;

  const token = Symbol("auth-session");
  pendingToken = token;
  const request = Promise.resolve()
    .then(() => loadAuthClient())
    .then((authClient) => authClient.getSession())
    .then((result) => {
      const session = result.data ?? null;
      if (pendingToken === token) {
        cachedSession = session;
        notify();
      }
      return session;
    })
    .catch(() => {
      if (pendingToken === token) {
        notify();
      }
      return null;
    })
    .finally(() => {
      if (pendingToken === token) {
        pendingRequest = null;
        pendingToken = null;
      }
    });

  pendingRequest = request;
  return request;
}

export function refreshAuthSession() {
  invalidateAuthSession();
  return loadAuthSession();
}

function scheduleIdle(callback: () => void, delay: number) {
  if (typeof window.requestIdleCallback === "function") {
    const idleId = window.requestIdleCallback(callback, { timeout: delay });
    return () => window.cancelIdleCallback(idleId);
  }
  const timeoutId = window.setTimeout(callback, delay);
  return () => window.clearTimeout(timeoutId);
}

export function useAuthSession({ eager = false, idleDelay = 1_200 }: AuthSessionOptions = {}) {
  const mountedRef = useRef(false);
  const [state, setState] = useState<AuthSessionState>(() => {
    const session = readAuthSession();
    return { data: session ?? null, isPending: eager && session === undefined };
  });

  const ensureSession = useCallback(async () => {
    const cached = readAuthSession();
    if (cached !== undefined) {
      if (mountedRef.current) setState({ data: cached, isPending: false });
      return cached;
    }
    if (mountedRef.current) setState((current) => ({ ...current, isPending: true }));
    const session = await loadAuthSession();
    if (mountedRef.current) setState({ data: session, isPending: false });
    return session;
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    const unsubscribe = subscribeAuthSession(() => {
      const session = readAuthSession();
      if (mountedRef.current) setState({ data: session ?? null, isPending: false });
    });
    let cancel = () => {};
    if (eager) {
      void ensureSession();
    } else {
      cancel = scheduleIdle(() => void ensureSession(), idleDelay);
    }
    return () => {
      mountedRef.current = false;
      unsubscribe();
      cancel();
    };
  }, [eager, ensureSession, idleDelay]);

  return { ...state, ensureSession };
}
